import { Button, Card, CardActions, CardContent, CardHeader, CardMedia, Divider, List, ListItem, Typography, Tooltip } from "@mui/material";
import { Link } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { useState } from "react";
import { toast } from "react-toastify";
import { Service } from "../../../app/models/service";
import { AppDeleteModalProps } from "../../../app/models/appDeleteModalProps";
import agent from "../../../app/api/agent";
import { removeService } from "../servicesSlice";
import AppDeleteModal from "../../../app/components/AppDeleteModal";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import AutocompleteInputOption from "../../../app/models/autocompleteInputOption";
import { currencyFormat } from "../../../app/util/util";
import ErrorIcon from '@mui/icons-material/Error';

interface Props {
    service: Service;
    handleCardSelectService?: (handledService: Service) => void;
    possibleSubservices: AutocompleteInputOption[];
}


export default function ServiceCard({ service, handleCardSelectService, possibleSubservices }: Props) {
    const dispatch = useAppDispatch();
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [deleteModalData, setModalData] = useState<AppDeleteModalProps | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const defaultServicePicture = `${import.meta.env.VITE_URL}images/defaultServicePicture.webp`;
    const { user } = useAppSelector(state => state.account);
    const [loadingPdf, setLoadingPdf] = useState(false);

    // Sprawdzenie czy usługa ma dane testowe
    const hasTestData = service.isGraphical
        ? Boolean(service.packageTestPath && service.packageTestPath.trim() !== '')
        : (() => {
            // W przypadku usług niegraficznych sprawdzamy questionsJson
            if (!service.questionsJson) return false;

            // Jeśli questionsJson jest tablicą
            if (Array.isArray(service.questionsJson)) {
                return service.questionsJson.length > 0;
            }

            // Jeśli questionsJson jest obiektem (może być pusty obiekt lub obiekt z pytaniami)
            if (typeof service.questionsJson === 'object') {
                return Object.keys(service.questionsJson).length > 0;
            }

            // Jeśli questionsJson jest stringiem (może być pusty string, "{}" lub JSON)
            if (typeof service.questionsJson === 'string') {
                if (service.questionsJson === '{}' || service.questionsJson.trim() === '')
                    return false;

                try {
                    const parsedJson = JSON.parse(service.questionsJson);
                    return Array.isArray(parsedJson) ? parsedJson.length > 0 : Object.keys(parsedJson).length > 0;
                } catch (e) {
                    console.error("Error parsing questionsJson string:", e);
                    return false;
                }
            }

            return false;
        })();

    // Tekst tooltipa zależny od typu usługi
    const missingTestDataTooltip = service.isGraphical
        ? "Brakuje wzoru arkusza egzaminacyjnego dla tej usługi"
        : "Brakuje pytań testowych dla tej usługi";

    // Poprawiona funkcja obsługująca generowanie PDF
    function handleGenerateTestPdf() {
        // Sprawdź ponownie, czy usługa ma dane testowe
        if (!hasTestData) {
            if (service.isGraphical) {
                toast.error('Nie można pobrać arkusza - brak arkusza dla tej usługi');
            } else {
                toast.error('Nie można wygenerować testu - brak pytań testowych');
            }
            return;
        }

        setLoadingPdf(true);
        agent.Services.generateTestPdf(service.id)
            .then(() => {
                // Komunikat o sukcesie
                if (service.isGraphical) {
                    toast.success('Arkusz egzaminacyjny został pobrany pomyślnie');
                } else {
                    toast.success('Test został wygenerowany pomyślnie');
                }
            })
            .catch(error => {
                // Komunikat o błędzie
                if (service.isGraphical) {
                    toast.error('Nie można pobrać arkusza egzaminacyjnego');
                } else {
                    toast.error('Nie można wygenerować testu');
                }
                console.error(error);
            })
            .finally(() => {
                setLoadingPdf(false);
            });
    }

    function handleOpenDeleteModal(props: AppDeleteModalProps) {
        setModalData(props);
        setOpenDeleteModal(true);
    }

    function handleCloseDeleteModal() {
        setOpenDeleteModal(false);
    }

    function handleDeleteService(id: number) {
        setLoading(true);
        if (id) {
            agent.Services.delete(id)
                .then(() => {
                    console.log(id)
                    dispatch(removeService(id));
                    toast.success('Usługa została usunięta')
                })
                .catch(error => {
                    toast.error('Podczas usuwania danych wystąpił błąd');
                    console.log(error);
                })
                .finally(() => setLoading(false));
            handleCloseDeleteModal();
            setModalData(undefined);
        }
    }

    function filterOptionsByIds(options: AutocompleteInputOption[], idsString: string): AutocompleteInputOption[] {
        // Rozdzielenie id stringa na tablicę id jako liczby
        const ids = idsString.split(',').map(idStr => Number(idStr.trim()));

        // Zwraca tylko te opcje, które mają id obecne w tablicy id
        return options.filter(option => ids.includes(option.id));
    }

    const filteredSubservicesOptions = filterOptionsByIds(possibleSubservices, service.subservices);

    return (
        <Card
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {service.imagePath === '' || service.imagePath === null || service.imagePath === undefined ?
                <CardMedia
                    sx={{ height: 140 }}
                    image={defaultServicePicture}
                    title={service.name}
                />
                :
                <CardMedia
                    sx={{ height: 140 }}
                    image={service.imagePath}
                    title={service.name}
                />
            }
            <CardHeader
                title={service.name}
                titleTypographyProps={{
                    sx: { fontWeight: 'bold', color: 'secondary' }
                }}
                action={
                    // Ikona wykrzyknika po prawej stronie, tylko dla administratorów i tylko gdy brak danych testowych
                    user && user.roles?.includes('Admin') && !hasTestData ? (
                        <Tooltip title={missingTestDataTooltip}>
                            <ErrorIcon color="error" sx={{ mt: 1, mr: 1 }} />
                        </Tooltip>
                    ) : null
                }
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography gutterBottom variant="h6">Cena: {currencyFormat(service.basePrice)}</Typography>
                <Typography gutterBottom variant="h6">{service.category}</Typography>
                <Typography gutterBottom variant="h6">Podusługi (moduły):</Typography>
                <List>
                    {filteredSubservicesOptions.map(subservice => (
                        <ListItem key={subservice.id}>{subservice.name}</ListItem>
                    ))}
                </List>
            </CardContent>
            <CardActions>
                <Button
                    sx={{ mr: 1, width: '100px', height: '40px' }}
                    variant='outlined'
                    component={Link} to={`/services/${service.id}`}
                    size="small"
                    title='Więcej'
                >Więcej</Button>
                {user && user.roles?.includes('Admin') && (
                    <>
                        <Button
                            sx={{ mr: 1, width: '100px', height: '40px' }}
                            variant='outlined'
                            onClick={() => handleCardSelectService!(service)}
                            size="small"
                            title='Edycja'
                            color='warning'
                        >Edycja</Button>

                        <LoadingButton
                            sx={{ mr: 1, width: '140px', height: '40px' }}
                            variant='outlined'
                            loading={loadingPdf}
                            disabled={!hasTestData}
                            size='small'
                            color='info'
                            onClick={handleGenerateTestPdf}
                            title={service.isGraphical ? 'Pobierz arkusz' : 'Generuj test PDF'}
                        >
                            {service.isGraphical ? 'Pobierz arkusz' : 'Generuj test'}
                        </LoadingButton>

                        <LoadingButton
                            sx={{ mr: 1, width: '100px', height: '40px' }}
                            variant='outlined'
                            loading={loading}
                            size='small'
                            color='error'
                            onClick={
                                () => {
                                    handleOpenDeleteModal({
                                        itemDescriptionFirstLine: 'Czy na pewno chcesz usunąć usługę?:',
                                        itemDescriptionSecondLine: '# ' + service.id + ' ' + service.name,
                                        handleCloseModal: handleCloseDeleteModal,
                                        handleIntDeleteFunction: handleDeleteService,
                                        isIntDeleteFunction: true,
                                        intId: service.id
                                    })
                                }}
                            title='Usuń'
                        >Usuń</LoadingButton>
                    </>
                )}
                {openDeleteModal ? <AppDeleteModal modalDeleteModalProps={deleteModalData!} /> : ''}
            </CardActions>
        </Card >
    )
}